import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  CodeBlock,
  Stack,
  Text,
} from '@customerio/pluma-components/react';

type QueryErrorCopy = {
  /** One plain sentence for the customer. */
  summary: string;
  /** The server's own words, when they are not the summary already. */
  detail: string | null;
};

// A decode failure's detail comes from encoding/json with its own prefix.
const SPEC_REJECTION = /^invalid query spec: [^:]+: (?:json: )?(.+)$/s;
// Snowflake prefixes its messages with the error number and SQLSTATE.
const WAREHOUSE_ERROR = /^\d{6} \([0-9A-Z]{5}\):/;

function sentence(text: string): string {
  const trimmed = text.trim();
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

/**
 * What to show for a failed compile or run. The server's spec rejections are
 * written for a person, so the sentence stands alone and the spec path stays
 * in the details; a warehouse error is Snowflake's text, kept behind a plain
 * sentence for whoever wants the exact words.
 */
export function queryErrorCopy(message: string): QueryErrorCopy {
  const rejected = SPEC_REJECTION.exec(message);
  if (rejected?.[1] != null) return { summary: sentence(rejected[1]), detail: message };
  if (WAREHOUSE_ERROR.test(message) || message.includes('SQL compilation error')) {
    return { summary: 'The warehouse could not run this query as written.', detail: message };
  }
  return { summary: message, detail: null };
}

type QueryErrorProps = { message: string };

export function QueryError({ message }: QueryErrorProps) {
  const { summary, detail } = queryErrorCopy(message);
  return (
    <Stack gap="100">
      <Text size="sm" color="critical" role="alert">
        {summary}
      </Text>
      {detail == null ? null : (
        <Accordion>
          <AccordionTitle>
            <Text size="xs" color="secondary">
              Show details
            </Text>
          </AccordionTitle>
          <AccordionContent px="0" py="100">
            <CodeBlock code={detail} language="text" withLineNumbers={false} />
          </AccordionContent>
        </Accordion>
      )}
    </Stack>
  );
}
